export type TeachingPresentationAppearance = 'LIGHT' | 'DARK'
export type TeachingPresentationStyleId = 'classroom' | 'focus' | 'minimal'

export interface TeachingPresentationTheme {
  readonly accent: string
  readonly accentContrast: string
  readonly border: string
  readonly success: string
  readonly surface: string
  readonly surfaceRaised: string
  readonly text: string
  readonly textMuted: string
}

export interface TeachingPresentationStyle {
  readonly density: 'comfortable' | 'compact'
  readonly id: TeachingPresentationStyleId
  readonly label: string
  readonly themes: Readonly<Record<TeachingPresentationAppearance, TeachingPresentationTheme>>
}

interface TeachingPresentationThemeSeed extends Pick<TeachingPresentationTheme, 'accent' | 'accentContrast' | 'surface' | 'surfaceRaised' | 'text'> {
  readonly border?: string
  readonly success?: string
  readonly textMuted?: string
}

function createTeachingPresentationTheme({
  accent,
  accentContrast,
  border,
  surface,
  surfaceRaised,
  success,
  text,
  textMuted,
}: TeachingPresentationThemeSeed): TeachingPresentationTheme {
  return {
    accent,
    accentContrast,
    border: border ?? `${text}29`,
    success: success ?? 'var(--lms-color-success)',
    surface,
    surfaceRaised,
    text,
    textMuted: textMuted ?? `${text}99`,
  }
}

const classroomDarkTheme = createTeachingPresentationTheme({
  accent: '#fbbf24',
  accentContrast: '#111827',
  surface: '#111827',
  surfaceRaised: 'rgba(255, 255, 255, .1)',
  text: '#f9fafb',
})

const focusDarkTheme = createTeachingPresentationTheme({
  accent: '#67e8f9',
  accentContrast: '#082f49',
  surface: '#082f49',
  surfaceRaised: 'rgba(8, 47, 73, .82)',
  text: '#ecfeff',
})

const minimalDarkTheme = createTeachingPresentationTheme({
  accent: '#fb7185',
  accentContrast: '#1f2937',
  surface: '#1f2937',
  surfaceRaised: 'rgba(31, 41, 55, .92)',
  text: '#f8fafc',
})

const classroomLightTheme = createTeachingPresentationTheme({
  accent: 'var(--lms-color-brand-accent)',
  accentContrast: 'var(--lms-color-ink-inverse)',
  border: 'var(--lms-color-border-subtle)',
  surface: 'var(--lms-color-surface-canvas)',
  surfaceRaised: 'var(--lms-color-surface-card)',
  text: 'var(--lms-color-ink-strong)',
  textMuted: 'var(--lms-color-ink-muted)',
})

const focusLightTheme = createTeachingPresentationTheme({
  accent: 'var(--lms-color-learning-positive)',
  accentContrast: 'var(--lms-color-ink-inverse)',
  border: 'var(--lms-color-border-subtle)',
  surface: 'var(--lms-color-surface-cream)',
  surfaceRaised: 'var(--lms-color-surface-warm)',
  text: 'var(--lms-color-ink-strong)',
  textMuted: 'var(--lms-color-ink-muted)',
})

const minimalLightTheme = createTeachingPresentationTheme({
  accent: 'var(--lms-color-ink-strong)',
  accentContrast: 'var(--lms-color-ink-inverse)',
  border: 'var(--lms-color-border-strong)',
  surface: 'var(--lms-color-surface-muted)',
  surfaceRaised: 'var(--lms-color-surface-card)',
  text: 'var(--lms-color-ink-strong)',
  textMuted: 'var(--lms-color-ink-muted)',
})

export const teachingPresentationStyles: readonly TeachingPresentationStyle[] = [
  {
    density: 'comfortable',
    id: 'classroom',
    label: 'Classroom',
    themes: { DARK: classroomDarkTheme, LIGHT: classroomLightTheme },
  },
  {
    density: 'comfortable',
    id: 'focus',
    label: 'Focus',
    themes: { DARK: focusDarkTheme, LIGHT: focusLightTheme },
  },
  {
    density: 'compact',
    id: 'minimal',
    label: 'Minimal',
    themes: { DARK: minimalDarkTheme, LIGHT: minimalLightTheme },
  },
] as const

export function getTeachingPresentationStyle(styleId: TeachingPresentationStyleId) {
  return (
    teachingPresentationStyles.find((style) => style.id === styleId) ??
    teachingPresentationStyles[0]
  )
}

export function getTeachingPresentationTheme(
  styleId: TeachingPresentationStyleId,
  appearance: TeachingPresentationAppearance = 'LIGHT',
) {
  return getTeachingPresentationStyle(styleId).themes[appearance]
}
